export type DiffOp =
  | { type: "unchanged"; line: string }
  | { type: "added"; line: string }
  | { type: "removed"; line: string };

/**
 * Line-level diff between two strings via LCS dynamic programming.
 *
 * Adequate for prompt-sized inputs (≤ a few thousand lines). For the
 * authoring flow the prompts are ~50-200 lines, which fits in a 200×200
 * table — negligible cost.
 */
export function lineDiff(a: string, b: string): DiffOp[] {
  const linesA = a.split("\n");
  const linesB = b.split("\n");
  const n = linesA.length;
  const m = linesB.length;

  // LCS lengths table — dp[i][j] = LCS length of linesA[i..] and linesB[j..].
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (linesA[i] === linesB[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (linesA[i] === linesB[j]) {
      ops.push({ type: "unchanged", line: linesA[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ type: "removed", line: linesA[i] });
      i++;
    } else {
      ops.push({ type: "added", line: linesB[j] });
      j++;
    }
  }
  while (i < n) ops.push({ type: "removed", line: linesA[i++] });
  while (j < m) ops.push({ type: "added", line: linesB[j++] });
  return ops;
}

export function countChanges(ops: DiffOp[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const op of ops) {
    if (op.type === "added") added++;
    else if (op.type === "removed") removed++;
  }
  return { added, removed };
}
